import { useCallback, useMemo } from 'react';

import { getPromotionEndDate, getUnitType } from '../OrderSummaryPage.utils';

import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { BACKSPLASH_ID, WATERFALL_ID } from '~/core/constants';
import { useSmallJobs } from '~/core/hooks/useSmallJobs';
import { OptionalProduct } from '~/core/models';
import { formatPriceUnitOrFree } from '~/core/utils';
import { usePricedProductsContext } from '~/legacy/easy-estimator-page/context';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import {
  CustomLaborData,
  selectPickedCustomLaborOptionalItems,
} from '~/redux/slices/optionalItems';

// Constants for pricing overrides - centralized for future implementation
const PRICING_OVERRIDES = {
  DISCOUNT_OVERRIDE_AMOUNT: 0, // for future implementation on "discount override"
  INSTALLATION_COST: 0, // for possible implementation of non included costs
  DELIVERY_COST: 0, // for future implementation on "mileage"
} as const;

type OrderInvoiceItem = {
  label: string;
  notes?: string[];
  measure: string;
  price: string;
  showLineItemQuantityWarning?: boolean;
};

type UseOrderInvoiceReturn = {
  subtotal: number;
  savings: number;
  displayTotal: string;
  items: OrderInvoiceItem[];
  promotionEndDate?: string;
};

/**
 * Custom hook for managing order invoice data and calculations
 *
 * Processes priced products, calculates totals including savings, and formats
 * invoice items for display. Handles small job scenarios with quantity warnings
 * and pricing adjustments.
 *
 * @returns {UseOrderInvoiceReturn} Object containing invoice items, totals, and metadata
 */
export const useOrderInvoice = (): UseOrderInvoiceReturn => {
  const {
    pricedProducts,
    priceTotalsWithDelivery: {
      priceWithoutDiscount: subtotal,
      priceWithDiscount,
    },
  } = usePricedProductsContext();
  const selectedColor = useAppSelector(selectSelectedColor);
  const selectedOptionalProducts = useAppSelector(
    selectPickedCustomLaborOptionalItems
  );
  const { useMinQtyAreaForPricing } = useSmallJobs();

  const { optionalItems } = useOptionalItems();

  /**
   * Helper function to generate formatted notes for a specific product
   * Extracted for better separation of concerns and testability
   *
   * @param productId - The ID of the product to get notes for
   * @param selectedItems - Array of selected items for the product
   * @param optionalItemsList - Array of available optional items
   * @returns Array of formatted note strings with pricing information
   */
  const getItemNotesForProduct = useCallback(
    (
      productId: number,
      selectedItems: CustomLaborData[],
      optionalItemsList: OptionalProduct[]
    ) => {
      const item = optionalItemsList.find(
        (item) => item.productId === productId
      );

      if (!item?.price) {
        return [];
      }

      return selectedItems
        .filter(({ quantity }) => quantity > 0) // More explicit check
        .map(
          ({ quantity, notes }) =>
            `($${(item.price * quantity).toFixed(2)}) ${notes}`
        );
    },
    []
  );

  /**
   * Generates formatted notes for optional product items with pricing details
   *
   * @param productId - The ID of the product to get notes for
   * @returns Array of formatted note strings with pricing information
   *
   * @example
   * // Returns: ["($150.00) Custom edge work", "($75.00) Additional cutout"]
   * const notes = getItemNotes(123);
   */
  const getItemNotes = useCallback(
    (productId: number): string[] => {
      const selectedItems = selectedOptionalProducts?.[productId] || [];

      return getItemNotesForProduct(productId, selectedItems, optionalItems);
    },
    [optionalItems, selectedOptionalProducts, getItemNotesForProduct]
  );

  const items: OrderInvoiceItem[] = useMemo(
    () =>
      pricedProducts.map((product) => ({
        label: product.consumerDesc,
        measure: `${product.quantity.toFixed(0)} ${getUnitType(product)}`,
        price: formatPriceUnitOrFree(
          product.priceWithoutDiscount || product.price
        ),
        notes: getItemNotes(product.productId),
        showLineItemQuantityWarning:
          useMinQtyAreaForPricing &&
          (product.name === selectedColor?.name ||
            product.lineItemId === BACKSPLASH_ID ||
            product.lineItemId === WATERFALL_ID),
      })),
    [getItemNotes, pricedProducts, selectedColor?.name, useMinQtyAreaForPricing]
  );

  const savings = subtotal - priceWithDiscount;

  const finalCalculatedTotal = Math.max(
    subtotal -
      savings -
      PRICING_OVERRIDES.DISCOUNT_OVERRIDE_AMOUNT +
      PRICING_OVERRIDES.INSTALLATION_COST +
      PRICING_OVERRIDES.DELIVERY_COST,
    0
  );

  const formattedTotalPrice = formatPriceUnitOrFree(finalCalculatedTotal);

  const displayTotal = useMinQtyAreaForPricing
    ? `${formattedTotalPrice}*`
    : formattedTotalPrice;

  return {
    subtotal,
    savings,
    displayTotal,
    items,
    promotionEndDate: getPromotionEndDate(pricedProducts),
  };
};
